import { useMemo, useState } from "react";
import { createdViaLabel } from "@/lib/buyerCreatedVia";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerAssignmentsApi,
  buyersApi,
  type BuyerAssignmentQueueParams,
  type BuyerAutoDistributeResultDto,
  type BuyerLeadListParams,
  type BuyerLeadSummaryDto,
  type BuyerSpecialistRosterRowDto,
} from "@/api/buyers";
import type { BuyerTeamSummaryDto } from "@/api/buyerTeams";
import { AutoDistributePreviewModal } from "@/components/buyers/AutoDistributePreviewModal";
import { ListFilterBar } from "@/components/layout/ListFilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import {
  Button,
  ColumnChooser,
  EmptyState,
  FilterChips,
  Input,
  Label,
  ListToolbar,
  MissingValue,
  Skeleton,
  StatCard,
  StatsBar,
  StatusBadge,
  SubTable,
  Timestamp,
  type ActiveFilterChip,
  type SubTableColumn,
} from "@/design-system/components/ui";
import {
  buyerAllocationKeys,
  buyerAssignmentKeys,
  buyerKeys,
  buyerTeamKeys,
} from "@/hooks/buyers/buyerKeys";
import { useBuyerTeams } from "@/hooks/buyers/useBuyerTeams";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useListFilters } from "@/hooks/useListFilters";
import { anyFilterActive, buyerLabel } from "./buyerFilters";
import { BuyerPager } from "./BuyerPager";
import { buyerStatusTone } from "./buyerListBits";
import { DEFAULT_QUEUE_VIEW, isSuspectedDuplicate, QUEUE_LIST_FILTERS, QUEUE_VIEWS } from "./queueFilters";
import { InlineQueryError, QueryErrorState } from "./queryStates";

/** The server's page size for the queue, and therefore the threshold that keeps
 *  `SubTable` from paging an already-paged page. `BuyerPager` owns the real one. */
const QUEUE_PAGE_SIZE = 25;

/** Where a manager goes to give somebody a buyer role. Linked from the empty
 *  roster state, and only for a caller who can actually open it. */
const USERS_ADMIN_PATH = "/admin/users";

/** Per-user preference keys (Q7). */
const VIEW_PREFERENCE = "queue-list-view";
const COLUMNS_PREFERENCE = "queue-list-columns";

/** The columns the reader may hide. The select control, the identity column
 *  and the actions are locked — the queue exists to assign, and a row that
 *  cannot be ticked or assigned is not a queue row. */
const CHOOSABLE_COLUMNS = [
  { key: "select", label: "Select", locked: true },
  { key: "buyer", label: "Buyer", locked: true },
  { key: "status", label: "Status" },
  { key: "source", label: "Source" },
  { key: "advertisement", label: "Advertisement" },
  { key: "team", label: "Team" },
  { key: "waiting", label: "Waiting since" },
  { key: "assign", label: "Actions", locked: true },
];

const ROLE_LABEL: Record<BuyerSpecialistRosterRowDto["buyerRoleCode"], string> = {
  F2: "Consultant",
  F1: "Manager",
};

/** One option label, shared by both pickers so the two can never describe the
 *  same person differently. The load rides the label because the whole point of
 *  offering everyone is that the assigner sees who is carrying what. */
function rosterOptionLabel(s: BuyerSpecialistRosterRowDto): string {
  return `${s.displayName ?? s.specialistUserId} — ${s.activeBuyers} active`;
}

/**
 * The one specialist `<select>`, used by the bulk bar and the per-row modal.
 *
 * <p>🔴 <b>Managers are offered, but under their own `optgroup`.</b> A Buyer
 * Manager may take a buyer directly; grouping makes choosing one a deliberate
 * act rather than an accidental one. Native `<optgroup>` rather than a new
 * component — it is exactly this, and it is keyboard- and screen-reader-native.</p>
 *
 * <p>Rendering nothing when the roster is empty is deliberate: an empty select
 * is the shape this page shipped with, and it is the thing that said nothing.
 * The caller renders an explanatory state instead.</p>
 */
function SpecialistSelect({
  roster,
  value,
  onChange,
  ariaLabel,
  placeholder,
}: {
  roster: BuyerSpecialistRosterRowDto[];
  value: string;
  onChange: (specialistUserId: string) => void;
  ariaLabel: string;
  placeholder: string;
}) {
  const specialists = roster.filter((s) => s.buyerRoleCode === "F2");
  const managers = roster.filter((s) => s.buyerRoleCode === "F1");

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className="h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
    >
      <option value="">{placeholder}</option>
      {specialists.length > 0 && (
        <optgroup label="Buyer consultants">
          {specialists.map((s) => (
            <option key={s.specialistUserId} value={s.specialistUserId}>
              {rosterOptionLabel(s)}
            </option>
          ))}
        </optgroup>
      )}
      {managers.length > 0 && (
        <optgroup label="Buyer managers">
          {managers.map((s) => (
            <option key={s.specialistUserId} value={s.specialistUserId}>
              {rosterOptionLabel(s)}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

/**
 * The manager's unassigned queue (L35).
 *
 * Every new buyer lands here regardless of source — including one a specialist
 * created by hand — so this is the single place new work appears. Alongside it,
 * each specialist's current load, so the manager assigns against what people
 * are actually carrying rather than from memory.
 */
export function BuyerAssignmentQueuePage() {
  const filters = useListFilters(QUEUE_LIST_FILTERS);
  const { values, page } = filters;
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const { hasPermission } = useAuth();

  /**
   * 🔴 Whether the caller DISPATCHES ACROSS TEAMS, which is a different question
   * from whether they may assign at all — the queue endpoint gates on
   * `Buyers.ManageAll` and every Buyer Manager holds it. This one decides
   * whether the queue they are looking at is merged across teams, and therefore
   * whether a team column, a team filter and a Send-to-team action mean
   * anything. It is chrome: the server scopes the queue from the caller's own
   * membership either way, and re-checks this permission on the write.
   */
  const canManageAllTeams = hasPermission("Buyers.ManageAllTeams");

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [specialist, setSpecialist] = useState("");
  const [preview, setPreview] = useState<BuyerAutoDistributeResultDto | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  /** The one buyer the per-row Assign action is placing, or null. */
  const [assignTarget, setAssignTarget] = useState<BuyerLeadSummaryDto | null>(null);

  // Q7 — the last view and the hidden columns, per user.
  const viewPref = useUserPreference<string>(VIEW_PREFERENCE, "");
  const columnsPref = useUserPreference<string[]>(COLUMNS_PREFERENCE, []);
  const hiddenColumnKeys = useMemo(
    () => (Array.isArray(columnsPref.value) ? columnsPref.value.filter((k) => typeof k === "string") : []),
    [columnsPref.value],
  );

  // URL choice → persisted preference → the default. `view` is never written
  // to the URL at its default, so a bare `/buyer-assignments` resolves through
  // the preference and a shared `?view=` link opens the same rows for anyone.
  const activeView =
    values.view ||
    (QUEUE_VIEWS.some((v) => v.key === viewPref.value) ? viewPref.value : "") ||
    DEFAULT_QUEUE_VIEW;
  const view = QUEUE_VIEWS.find((v) => v.key === activeView) ?? QUEUE_VIEWS[0];
  // The server's stale flag: the "Waiting too long" view, the stat card and the
  // primary-row chip all read and write this ONE value.
  const staleOnly = values.staleOnly || Boolean(view.params.staleOnly);

  const selectView = (key: string) => {
    filters.set("view", key);
    viewPref.set(key);
  };

  const params = useMemo<BuyerAssignmentQueueParams>(
    () => ({
      // The view's own params sit UNDER everything explicit.
      ...view.params,
      page,
      pageSize: QUEUE_PAGE_SIZE,
      q: values.q || undefined,
      source: values.source ? [values.source] : undefined,
      staleOnly: staleOnly ? true : undefined,
      // Sent regardless of the permission — the server ignores it for a caller
      // who is not an all-teams holder rather than trusting the client to have
      // withheld it. Blank means "no narrowing", which for an all-teams holder
      // is the merged queue (D6).
      buyerTeamId: values.buyerTeamId || undefined,
    }),
    [page, values, view, staleOnly],
  );

  const queue = useQuery({
    queryKey: buyerAssignmentKeys.queue(params),
    queryFn: () => buyerAssignmentsApi.queue(params),
    // Wait for the persisted view: firing with the fallback and again with the
    // preference would be the double-fetch class, one render apart.
    enabled: !viewPref.isLoading,
  });

  // Open teams only: an archived team still holds its leads and still accepts a
  // routed one, but it is deliberately out of the PICKER — which is what this
  // feeds, twice (the filter and the Send-to-team destination). Read for every
  // caller and DISCARDED for one who can see neither control: the hook takes no
  // `enabled`, and the alternative is a conditional hook, which React forbids.
  const teams = useBuyerTeams(false);
  const teamRows: BuyerTeamSummaryDto[] = canManageAllTeams ? teams.data ?? [] : [];
  const teamNameById = useMemo(
    () => new Map(teamRows.map((t) => [t.id, t.name])),
    [teamRows],
  );

  /** The team a Send-to-team run is moving the selection to, or null. */
  const [sendTarget, setSendTarget] = useState<string>("");
  /** The one buyer a per-row Send to team is moving, or null. */
  const [sendRow, setSendRow] = useState<BuyerLeadSummaryDto | null>(null);

  // 🔴 Memoised on the query result rather than written inline — `?? []` in a
  // render body allocates a fresh array every render while the query is in
  // flight (see `buyerSelection.ts` for what that cost once).
  const fetched = useMemo(() => queue.data?.buyers ?? [], [queue.data]);
  // The advertisement options are the distinct names on the rows in hand — the
  // row carries a name, not an id the server would key on.
  const advertisementNames = useMemo(
    () =>
      [...new Set(fetched.map((b) => b.advertisementName).filter((n): n is string => Boolean(n)))].sort(),
    [fetched],
  );
  // The two page-local narrowings — the duplicates view and the advertisement
  // filter. Neither has a server param (see `queueFilters.ts`), so both are
  // taken over the page in hand and said so beside the table.
  const narrowsClientSide = activeView === "duplicates" || Boolean(values.advertisement);
  const rows = useMemo(
    () =>
      fetched.filter(
        (b) =>
          (activeView !== "duplicates" || isSuspectedDuplicate(b)) &&
          (!values.advertisement || b.advertisementName === values.advertisement),
      ),
    [fetched, activeView, values.advertisement],
  );
  const roster = queue.data?.specialistRoster ?? [];
  // 🔴 The tile counts what its label says. A count and its label must be
  // derived from the same predicate, and "Consultants" means F2 — managers are
  // offerable but they are not consultants. A combined number, if it is ever
  // wanted, is a second tile rather than a redefinition of this one.
  const specialistCount = roster.filter((s) => s.buyerRoleCode === "F2").length;

  const bulkAssign = useMutation({
    mutationFn: () =>
      buyerAssignmentsApi.bulkAssign({
        specialistUserId: specialist,
        buyerLeadIds: [...selected],
        // 🔴 Minted ONCE per attempt and reused if the caller retries. The grant
        // ledger is append-only, so a partially-failed bulk assign that is
        // retried without a stable key double-credits the specialist — and on
        // an append-only table there is no way to clean that up afterwards.
        idempotencyKey: idempotencyKeyFor(specialist, selected),
      }),
    onSuccess: async (result) => {
      setSelected(new Set());
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      // The buyers list shows the same rows with an owner now, and its stat
      // cards count the unassigned — both are stale the moment this succeeds.
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      // Entitlement is COMPUTED per request from the qualifying stamps rather
      // than stored, so handing a specialist forty buyers moves their standing
      // — and this was the one mutation on the page that did not say so. Its
      // two siblings (auto-distribute, and the per-buyer assign added here) do.
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
      // A bulk assign reports per item rather than failing wholesale, so a
      // partial result is a normal outcome and the dialog has to say which
      // parts happened — "Assigned" alone would hide the failures.
      await dialogs.alert({
        title: result.failed > 0 ? "Assigned, with failures" : "Assigned",
        tone: result.failed > 0 ? "error" : "success",
        message:
          `${result.assigned} assigned` +
          (result.skipped ? `, ${result.skipped} skipped` : "") +
          (result.failed ? `, ${result.failed} failed` : "") +
          ".",
      });
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Assign failed",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  /**
   * Place ONE named buyer with ONE named specialist.
   *
   * Distinct from `bulkAssign` in more than shape: a bulk assign sends one
   * specialist for a whole selection, so a manager who wants to give this buyer
   * to Ada and that one to Sam has to run it twice and tick carefully. This is
   * the deliberate placement — the decision a manager makes about a particular
   * buyer, which is most of what the queue is worked for.
   *
   * No idempotency key, deliberately: the ledger's stable-key guard exists for
   * the bulk path, where a retry after a PARTIAL failure would re-grant the
   * items that already succeeded. A single assign has no partial state, and the
   * service no-ops a re-assign to the current owner rather than writing a second
   * grant row.
   */
  const assignOne = useMutation({
    mutationFn: (v: { buyerLeadId: string; specialistUserId: string; reason: string }) =>
      buyerAssignmentsApi.assign(v.buyerLeadId, {
        specialistUserId: v.specialistUserId,
        // The server stores the reason on the audit entry and, on a REASSIGN,
        // on the grant row. Out of this queue there is never a prior owner, so
        // it is optional here — omitted rather than sent blank.
        reason: v.reason.trim() || undefined,
      }),
    onSuccess: async (_result, v) => {
      setAssignTarget(null);
      // Drop it from any pending checkbox selection: it has an owner now, and a
      // bulk assign that still carried it would ask the server to re-grant a
      // buyer this action already placed.
      setSelected((prev) => {
        if (!prev.has(v.buyerLeadId)) return prev;
        const next = new Set(prev);
        next.delete(v.buyerLeadId);
        return next;
      });
      // The same blast radius as an Auto-Distribute run, for the same reason:
      // the queue lost a row, the buyers list gained an owner, and the
      // specialist's standing moved — entitlement is computed from what they
      // hold, not stored, so nothing narrower would reach it.
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Assign failed",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  /**
   * Move leads into another team's queue (D7).
   *
   * <p>🔴 <b>It rewrites the TEAM and never the ASSIGNMENT.</b> A lead already
   * being worked keeps its specialist, who may now be on a different team —
   * which is the ordinary cross-team holding D2 permits, not an error. The
   * warning below is advisory for exactly that reason: refusing would mean a
   * mis-routed lead could only be fixed by unassigning somebody's work
   * first.</p>
   *
   * <p>Gated on `Buyers.ManageAllTeams` and re-checked in the service. Hiding
   * the control is not the gate.</p>
   */
  const sendToTeam = useMutation({
    mutationFn: (v: { buyerLeadIds: string[]; buyerTeamId: string }) =>
      buyerAssignmentsApi.sendToTeam({
        buyerLeadIds: v.buyerLeadIds,
        buyerTeamId: v.buyerTeamId,
      }),
    onSuccess: async (result) => {
      setSelected(new Set());
      setSendRow(null);
      setSendTarget("");
      // The queue lost rows to another team's, and the buyers list renders the
      // team on every row. Not the allocation prefix: a team move touches no
      // owner, and entitlement is computed from what a PERSON holds (D2), so
      // nothing there can have changed.
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      // Every team's lead rollups moved — the one they left and the one they
      // arrived at — and the registry renders both.
      await qc.invalidateQueries({ queryKey: buyerTeamKeys.all });
      await dialogs.alert({
        title: result.skipped > 0 ? "Sent, with skips" : "Sent",
        tone: result.skipped > 0 ? "error" : "success",
        message:
          `${result.moved} moved` +
          (result.skipped ? `, ${result.skipped} skipped` : "") +
          ".",
      });
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Couldn't send to team",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  /**
   * Return a buyer to the queue.
   *
   * The reason is REQUIRED — the server refuses a blank one, because taking work
   * off someone is a decision worth a sentence and the grant ledger is
   * append-only, so the offsetting row it writes is the only place that sentence
   * can ever live.
   */
  const unassignOne = useMutation({
    mutationFn: (v: { buyerLeadId: string; reason: string }) =>
      buyerAssignmentsApi.unassign(v.buyerLeadId, { reason: v.reason }),
    onSuccess: async () => {
      // Identical to an assign: this moves a buyer between the queue and a
      // specialist's book, just in the other direction.
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Couldn't return the buyer to the queue",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  /** Ask for the reason the server requires, then return the buyer. A cancelled
   *  prompt resolves null and nothing is sent. */
  const askThenUnassign = async (buyerLeadId: string, displayName: string) => {
    const reason = await dialogs.prompt({
      title: `Return ${displayName} to the queue?`,
      label: "Reason",
      placeholder: "Why is this buyer being returned?",
      confirmLabel: "Return to queue",
    });
    if (!reason) return;
    unassignOne.mutate({ buyerLeadId, reason });
  };

  // Auto-Distribute is a TWO-CALL flow and the first call is the whole safety
  // property: the run touches every unassigned buyer at once, so the manager
  // confirms a plan rather than discovering one. The dry run is the server's own
  // — same code path, `preview: true` — so what is shown is what would happen,
  // not a client-side guess at it.
  const previewDistribute = useMutation({
    mutationFn: () => buyerAssignmentsApi.autoDistribute({ preview: true }),
    onSuccess: (result) => {
      // no-invalidation: a preview run assigns nothing and writes nothing, so
      // there is no server state for any cached read to have gone stale against.
      setPreview(result);
      setPreviewOpen(true);
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Couldn't build the plan",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  const applyDistribute = useMutation({
    mutationFn: () =>
      buyerAssignmentsApi.autoDistribute({
        preview: false,
        reason: "Auto-distributed from the assignment queue.",
      }),
    onSuccess: async (result) => {
      setPreviewOpen(false);
      setPreview(null);
      // Everything the run touched: the queue it drained, the buyers that now
      // have an owner, and every specialist's standing — entitlement is computed
      // from what they hold, so an assignment moves it.
      await qc.invalidateQueries({ queryKey: buyerAssignmentKeys.all });
      await qc.invalidateQueries({ queryKey: buyerKeys.all });
      await qc.invalidateQueries({ queryKey: buyerAllocationKeys.all });
      await dialogs.alert({
        title: result.assigned > 0 ? "Distributed" : "Nothing was assigned",
        tone: result.assigned > 0 ? "success" : "error",
        message:
          `${result.assigned} assigned` +
          (result.skipped ? `, ${result.skipped} still waiting` : "") +
          ".",
      });
    },
    onError: (e: Error) => {
      void dialogs.alert({
        title: "Auto-distribute failed",
        tone: "error",
        message: e.message ?? "Please try again.",
      });
    },
  });

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allShownSelected = rows.length > 0 && rows.every((b) => selected.has(b.id));
  const toggleAll = () =>
    setSelected(allShownSelected ? new Set() : new Set(rows.map((b) => b.id)));

  const canAssign = selected.size > 0 && specialist.length > 0 && !bulkAssign.isPending;

  /**
   * 🔴 Leads in the current selection whose OWNER is not on the destination
   * team — advisory, never blocking (D2).
   *
   * <p>A lead keeps its specialist when it changes team, so this is a legitimate
   * end state and the server allows it. Saying so before the move is what makes
   * it a considered choice rather than a surprise; refusing it would mean a
   * mis-routed lead could only be fixed by unassigning somebody's work first.</p>
   *
   * <p>Derived from the queue's OWN roster, which is the set of people on the
   * caller's currently-scoped team — so on the merged all-teams queue it is
   * everybody eligible, and a lead held by a specialist the roster does not list
   * is the case worth flagging. The queue only ever lists UNASSIGNED buyers
   * today, so in practice this is empty; it is written against
   * `assignedSpecialistUserId` rather than against that fact, because a queue
   * that later showed owned rows would otherwise move them silently.</p>
   */
  const crossTeamHolders = useMemo(() => {
    if (!sendTarget) return [];
    const onRoster = new Set(roster.map((r) => r.specialistUserId));
    return rows.filter(
      (b) =>
        selected.has(b.id) &&
        b.assignedSpecialistUserId &&
        !onRoster.has(b.assignedSpecialistUserId),
    );
  }, [rows, roster, selected, sendTarget]);

  // Row 3 of the toolbar — everything narrowing the queue that is not visible
  // at rest. Search, the waiting chip and the team are on screen; these are
  // behind "More filters".
  const activeChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (values.source)
      chips.push({ key: "source", label: `Source: ${buyerLabel(values.source)}`, onRemove: () => filters.set("source", "") });
    if (values.advertisement)
      chips.push({ key: "advertisement", label: `Advertisement: ${values.advertisement}`, onRemove: () => filters.set("advertisement", "") });
    return chips;
  }, [values.source, values.advertisement, filters]);

  /*
    🔴 BRANCHED ON THE SERVER'S SCOPE KIND, never re-derived from the counts. A
    caller on no team and a caller whose team is genuinely clear both get zero
    rows and an empty roster — and "The queue is clear. Every buyer has an
    owner." is a false statement to the first of them, which is exactly what
    this page used to say, followed by "No specialists to show" underneath it.
    One honest state for the whole body, and none of the tiles, the filters,
    the roster or the Auto-distribute action, every one of which would be
    describing a queue the caller does not have (D5 #1 / Q7).
  */
  if (queue.data?.scopeKind === "NoScope") {
    return (
      <>
        <PageHeader
          eyebrow="Buyer CRM"
          title="Assignment queue"
          description="New buyers waiting for an owner, and what each consultant is currently carrying."
        />
        <div data-testid="assignment-queue-no-scope">
          <EmptyState
            title="You are not on a buyer team"
            description="The queue is scoped to a team, and you are not a member of one. Ask an administrator to add you to a team (or, if you manage a team, to make you a member of it)."
          />
        </div>
      </>
    );
  }

  return (
    <>
      {/*
        Auto-Distribute acts on the WHOLE unassigned queue, not on a selection —
        so it lives in the header, reachable with nothing ticked, rather than in
        the bulk bar that only appears once rows are picked.
      */}
      <PageHeader
        eyebrow="Buyer CRM"
        title="Assignment queue"
        description="New buyers waiting for an owner, and what each consultant is currently carrying."
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              onClick={() => previewDistribute.mutate()}
              disabled={previewDistribute.isPending || applyDistribute.isPending}
            >
              {previewDistribute.isPending ? "Building plan…" : "Auto-distribute"}
            </Button>
            <Link
              to="/buyers/allocation"
              className="text-small text-gold-shadow hover:underline"
            >
              Allocation policy
            </Link>
          </div>
        }
      />

      <StatsBar columns={3}>
        <StatCard label="Unassigned" value={queue.data?.totalUnassigned ?? "—"} />
        <StatCard
          label={`Waiting over ${queue.data?.staleAfterDays ?? "—"} days`}
          value={queue.data?.staleCount ?? "—"}
          onClick={() => filters.set("staleOnly", !staleOnly)}
          active={staleOnly}
        />
        <StatCard label="Consultants" value={specialistCount || "—"} />
      </StatsBar>

      {/* §3 — one band: search; views | waiting chips + team | More filters |
          columns; the applied chips. The queue is never large enough to earn
          §5's threshold, so search is always on — it is how a manager finds the
          one row somebody just phoned about. */}
      <ListToolbar
        data-testid="queue-toolbar"
        search={{
          value: filters.inputs.q,
          onChange: (next) => filters.set("q", next),
          placeholder: "Search name, company, email, phone…",
          label: "Search the queue",
        }}
        views={{
          items: QUEUE_VIEWS.map((v) => ({
            key: v.key,
            label: v.key === "waiting" && queue.data ? `Waiting over ${queue.data.staleAfterDays} days` : v.label,
            badge: v.key === "waiting" ? queue.data?.staleCount : undefined,
          })),
          activeKey: activeView,
          onSelect: selectView,
          label: "Queue views",
        }}
        primary={
          <>
            <FilterChips
              label="Age filter"
              activeId={staleOnly ? "1" : ""}
              onChange={(id) => filters.set("staleOnly", id === "1")}
              chips={[
                { id: "", label: "Waiting: all" },
                { id: "1", label: `Over ${queue.data?.staleAfterDays ?? "—"} days` },
              ]}
            />
            {/*
              The team filter, only for an all-teams holder: for anybody else
              the server has already scoped the queue to their own team, so a
              control offering to change it would offer something it cannot do.
              Its options are FETCHED, which is why it is not a descriptor.
            */}
            {canManageAllTeams && (
              <div className="flex items-center gap-space-2">
                <label htmlFor="queue-team-filter" className="text-caption text-gray-mid">
                  Team
                </label>
                <select
                  id="queue-team-filter"
                  aria-label="Team filter"
                  className="h-10 w-52 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                  value={values.buyerTeamId}
                  onChange={(e) => filters.set("buyerTeamId", e.target.value)}
                >
                  <option value="">All teams</option>
                  {teamRows.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {teams.isError && (
                  <span className="text-caption text-status-error" role="alert">
                    Couldn't load teams.
                  </span>
                )}
              </div>
            )}
          </>
        }
        moreFilters={{
          activeCount: activeChips.length,
          content: (
            <div className="flex flex-col gap-space-3">
              <ListFilterBar filters={filters} className="mb-0" rows={[{ keys: ["source"] }]} />
              <div className="flex items-center gap-space-2">
                <label htmlFor="queue-advertisement-filter" className="text-caption text-gray-mid">
                  Advertisement
                </label>
                <select
                  id="queue-advertisement-filter"
                  aria-label="Advertisement filter"
                  className="h-10 w-52 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                  value={values.advertisement}
                  onChange={(e) => filters.set("advertisement", e.target.value)}
                >
                  <option value="">All advertisements</option>
                  {/* A deep-linked name that is on no row this page keeps its
                      option, so the control shows what it is filtering by. */}
                  {values.advertisement && !advertisementNames.includes(values.advertisement) && (
                    <option value={values.advertisement}>{values.advertisement}</option>
                  )}
                  {advertisementNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ),
        }}
        activeChips={activeChips}
        onClearAllChips={() => {
          // ONE write — `set` builds its next params from THIS render's, so a
          // chain of sets would have the last overwrite the rest.
          filters.reset();
        }}
        actions={
          <ColumnChooser
            columns={CHOOSABLE_COLUMNS.filter((c) => c.key !== "team" || canManageAllTeams)}
            hiddenKeys={hiddenColumnKeys}
            onChange={columnsPref.set}
            data-testid="queue-columns"
          />
        }
      />

      {/* The narrowing is taken over the page in hand — the server has no
          param for either — so say how many of the rows fetched it kept. */}
      {narrowsClientSide && !queue.isLoading && !queue.isError && (
        <p className="mb-space-2 text-caption text-gray-mid" role="status" data-testid="queue-narrowing-caption">
          {rows.length} of the {fetched.length} waiting buyers on this page match.
        </p>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-space-3 mb-space-3 p-space-3 border border-gold rounded-md bg-white-warm">
          <span className="text-small">
            {selected.size} selected
          </span>
          <SpecialistSelect
            roster={roster}
            value={specialist}
            onChange={setSpecialist}
            ariaLabel="Assign to consultant"
            placeholder="Assign to…"
          />
          <Button size="sm" disabled={!canAssign} onClick={() => bulkAssign.mutate()}>
            {bulkAssign.isPending ? "Assigning…" : "Assign"}
          </Button>
          {canManageAllTeams && (
            <>
              <span className="text-caption text-gray-mid">or</span>
              <select
                aria-label="Send to team"
                className="h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                value={sendTarget}
                onChange={(e) => setSendTarget(e.target.value)}
              >
                <option value="">Send to team…</option>
                {teamRows.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                variant="secondary"
                disabled={!sendTarget || sendToTeam.isPending}
                onClick={() =>
                  sendToTeam.mutate({
                    buyerLeadIds: [...selected],
                    buyerTeamId: sendTarget,
                  })
                }
              >
                {sendToTeam.isPending ? "Sending…" : "Send"}
              </Button>
            </>
          )}
          <Button size="sm" variant="secondary" onClick={() => setSelected(new Set())}>
            Clear
          </Button>

          {/*
            🔴 A NOTICE, not a gate — the Send button above stays enabled. A lead
            keeps its specialist when it changes team (D2), so this is a
            legitimate end state that the server allows; saying so first is what
            makes it a considered choice. `role="status"` rather than "alert":
            this is information, not an error.
          */}
          {crossTeamHolders.length > 0 && (
            <p
              role="status"
              className="basis-full m-0 text-caption text-status-warning"
              data-testid="send-to-team-cross-team-warning"
            >
              {crossTeamHolders.length === 1
                ? `1 selected buyer is held by somebody who is not on ${teamNameById.get(sendTarget) ?? "that team"}. `
                : `${crossTeamHolders.length} selected buyers are held by somebody who is not on ${teamNameById.get(sendTarget) ?? "that team"}. `}
              They keep their current owner — only the queue changes.
            </p>
          )}
        </div>
      )}

      {queue.isLoading || viewPref.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : queue.isError ? (
        /*
          🔴 Checked BEFORE the empty branch, and the reason is the whole point:
          a failed queue read used to fall through to "The queue is clear. Every
          buyer has an owner." — telling a manager there is no work to do on the
          strength of a request that never completed. Nothing else on this page
          would have contradicted it.
        */
        <QueryErrorState
          what="the assignment queue"
          onRetry={() => void queue.refetch()}
          retrying={queue.isFetching}
        />
      ) : rows.length === 0 ? (
        narrowsClientSide || anyFilterActive(filters) ? (
          <EmptyState
            title="No waiting buyers match"
            description="Widen the filters, or choose the whole queue, to see the rest."
          />
        ) : (
          <EmptyState
            title="The queue is clear"
            description="Every buyer has an owner. New ones will appear here as they arrive."
          />
        )
      ) : (
        <SubTable<BuyerLeadSummaryDto>
          caption="Buyers waiting for an owner"
          data-testid="assignment-queue-table"
          rows={rows}
          rowKey={(b) => b.id}
          hiddenColumnKeys={hiddenColumnKeys}
          stickyHeader
          // The page pages SERVER-side (BuyerPager, below), so SubTable must
          // never page the page: its threshold is the page size, which the rows
          // in hand can never exceed.
          chromeThreshold={QUEUE_PAGE_SIZE}
          columns={queueColumns({
            allShownSelected,
            toggleAll,
            selected,
            toggle,
            onAssign: setAssignTarget,
            assigningId:
              assignOne.isPending ? assignOne.variables?.buyerLeadId ?? null : null,
            // The Team column and the per-row Send action are one permission's
            // worth of chrome: on a single-team queue every row carries the same
            // team, so a column repeating it would be a constant.
            showTeam: canManageAllTeams,
            onSendToTeam: setSendRow,
            sendingId: sendToTeam.isPending ? sendRow?.id ?? null : null,
          })}
        />
      )}

      {/*
        🔴 Page against the count that matches the LIST, not the headline count.
        The server computes both totals over the filter set MINUS the stale
        quick-filter (so the "N waiting" card never contradicts the list it
        filters to) — which means that with the stale filter on, totalUnassigned
        overcounts the rows below and the pager would offer pages past the end.
      */}
      <BuyerPager
        filters={filters}
        total={(staleOnly ? queue.data?.staleCount : queue.data?.totalUnassigned) ?? 0}
        shown={rows.length}
      />

      {/* The load table reads off the SAME query as the queue above, so a failed
          read is already reported there — rendering "no specialists" underneath
          it would be a second lie from one failure. */}
      {!queue.isError && (
        <SpecialistLoadTable
          roster={roster}
          onUnassign={askThenUnassign}
          unassigningId={
            unassignOne.isPending ? unassignOne.variables?.buyerLeadId ?? null : null
          }
        />
      )}

      <AutoDistributePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={preview}
        loading={previewDistribute.isPending}
        applying={applyDistribute.isPending}
        onConfirm={() => applyDistribute.mutate()}
      />

      {sendRow && (
        <SendToTeamModal
          key={sendRow.id}
          buyer={sendRow}
          teams={teamRows}
          teamsFailed={teams.isError}
          pending={sendToTeam.isPending}
          onCancel={() => setSendRow(null)}
          onConfirm={(buyerTeamId) =>
            sendToTeam.mutate({ buyerLeadIds: [sendRow.id], buyerTeamId })
          }
        />
      )}

      {assignTarget && (
        <AssignBuyerModal
          key={assignTarget.id}
          buyer={assignTarget}
          roster={roster}
          rosterFailed={queue.isError}
          onRetryRoster={() => void queue.refetch()}
          canManageUsers={hasPermission("Users.Read")}
          pending={assignOne.isPending}
          onCancel={() => setAssignTarget(null)}
          onConfirm={(specialistUserId, reason) =>
            assignOne.mutate({ buyerLeadId: assignTarget.id, specialistUserId, reason })
          }
        />
      )}
    </>
  );
}

/**
 * The per-row placement: one buyer, one specialist, optionally a note.
 *
 * The picker is fed from the queue's own roster rather than a second read — it
 * is already loaded, and showing what each person is carrying at the moment of
 * the decision is the whole reason the queue reports it.
 *
 * <p>🔴 <b>Three states, never a bare empty select.</b> This modal used to
 * render an empty `<select>` under the sentence "No specialist is carrying
 * buyers yet, so there is nobody to pick from here." That sentence was an
 * accurate description of the mechanism and therefore read as a designed empty
 * state — which is why it survived: a correct account of a defect's mechanism is
 * the most durable disguise a defect can wear, because it satisfies the reader
 * who goes looking for an explanation. It was in fact describing a bootstrap
 * deadlock, and it is deleted.</p>
 */
function AssignBuyerModal({
  buyer,
  roster,
  rosterFailed,
  onRetryRoster,
  canManageUsers,
  pending,
  onCancel,
  onConfirm,
}: {
  buyer: BuyerLeadSummaryDto;
  roster: BuyerSpecialistRosterRowDto[];
  /** The read failed, as distinct from succeeding and returning nobody. An
   *  empty list and a broken request must never render the same way. */
  rosterFailed: boolean;
  onRetryRoster: () => void;
  /** Gates the link out to the admin users page — offered only to a caller who
   *  can actually open it. */
  canManageUsers: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (specialistUserId: string, reason: string) => void;
}) {
  const [specialistUserId, setSpecialistUserId] = useState("");
  const [reason, setReason] = useState("");

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Assign ${buyer.displayName}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            disabled={!specialistUserId || pending}
            onClick={() => onConfirm(specialistUserId, reason)}
          >
            {pending ? "Assigning…" : "Assign buyer"}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <div className="grid gap-space-2">
          <Label required>Consultant</Label>
          {rosterFailed ? (
            /* State 3 — the read failed. Visibly distinct from an empty list,
               and recoverable without reopening the modal. */
            <InlineQueryError
              what="the consultant roster"
              onRetry={onRetryRoster}
              retrying={false}
            />
          ) : roster.length === 0 ? (
            /* State 1 — nobody holds a buyer role. Actionable, and it names the
               thing that would fix it. */
            <div data-testid="assign-no-roster">
              <p className="m-0 text-caption text-gray-mid">
                No user holds Buyer Consultant or Buyer Manager, so there is nobody
                to assign this buyer to.
              </p>
              {canManageUsers && (
                <Link
                  to={USERS_ADMIN_PATH}
                  className="mt-space-1 inline-block text-caption text-gold-shadow hover:underline"
                >
                  Give someone a buyer role
                </Link>
              )}
            </div>
          ) : (
            /* State 2 — the ordinary case. Everyone eligible, grouped, with the
               book each is carrying on the label. */
            <SpecialistSelect
              roster={roster}
              value={specialistUserId}
              onChange={setSpecialistUserId}
              ariaLabel="Consultant"
              placeholder="Choose a consultant…"
            />
          )}
        </div>

        <div className="grid gap-space-2">
          <Label>Note</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Note"
            placeholder="Why this consultant?"
          />
          {/* Optional out of the queue and only out of the queue: the server
              requires one when a buyer already has an owner, and nothing in
              this table does. */}
          <p className="text-caption text-gray-mid">
            Recorded against the assignment. Not required for a buyer waiting in the queue.
          </p>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The queue row.
 *
 * 🔴 THE ROWS STAY RICH — and the first column is a CONTROL, not a value. Bulk
 * select is the reason this screen exists (assigning forty buyers one at a time
 * is the workflow it replaces), so the checkbox column keeps its header
 * checkbox and every row keeps its own. It contributes nothing to search: a
 * checkbox has no text, and letting it fall back to the rendered node would
 * match every row on nothing.
 */
function queueColumns({
  allShownSelected,
  toggleAll,
  selected,
  toggle,
  onAssign,
  assigningId,
  showTeam,
  onSendToTeam,
  sendingId,
}: {
  allShownSelected: boolean;
  toggleAll: () => void;
  selected: Set<string>;
  toggle: (id: string) => void;
  onAssign: (buyer: BuyerLeadSummaryDto) => void;
  assigningId: string | null;
  /** Only on a merged all-teams queue — see the call site. */
  showTeam: boolean;
  onSendToTeam: (buyer: BuyerLeadSummaryDto) => void;
  sendingId: string | null;
}): SubTableColumn<BuyerLeadSummaryDto>[] {
  return [
    {
      key: "select",
      // 🔴 The header IS the select-all control. It belongs in the header of the
      // column it selects — floating it above the table leaves the reader to
      // infer what it selects, and this screen's whole reason for existing is
      // assigning forty buyers at once rather than one at a time.
      header: (
        <input
          type="checkbox"
          aria-label="Select every buyer on this page"
          checked={allShownSelected}
          onChange={toggleAll}
        />
      ),
      className: "w-space-6",
      searchText: () => "",
      render: (b) => (
        <input
          type="checkbox"
          checked={selected.has(b.id)}
          onChange={() => toggle(b.id)}
          aria-label={`Select ${b.displayName}`}
        />
      ),
    },
    {
      key: "buyer",
      header: "Buyer",
      searchText: (b) => `${b.staffDisplayName ?? b.displayName} ${b.email ?? ""}`,
      render: (b) => (
        <div className="min-w-0">
          <Link to={`/buyers/${b.id}`} className="text-gold-shadow hover:underline">
            {b.staffDisplayName ?? b.displayName}
          </Link>
          <div className="text-caption text-gray-mid">{b.email}</div>
          {/* The funnel creates rather than linking on an email match, so a
              repeat submitter arrives as a second row. That is the deliberate
              trade — a wrong link would put another person's financial position
              into a package sent to an outside broker — but it is only
              containment if the manager can see it before assigning both rows
              to different specialists. Amber: attention, not overdue. */}
          {b.suspectedDuplicateOfBuyerLeadId ? (
            <Link
              to={`/buyers/${b.suspectedDuplicateOfBuyerLeadId}`}
              className="text-caption text-status-warning hover:underline"
              data-testid="buyer-queue-suspected-duplicate"
            >
              Possible duplicate of {b.suspectedDuplicateOfName ?? "another buyer"}
            </Link>
          ) : null}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      searchText: (b) => buyerLabel(b.status),
      render: (b) => (
        <StatusBadge tone={buyerStatusTone(b.status)}>{buyerLabel(b.status)}</StatusBadge>
      ),
    },
    {
      key: "source",
      header: "Source",
      render: (b) => {
        // Z6d — the queue names the site alone; the owner is on the buyer page.
        const via = createdViaLabel(b, { withOwner: false });
        return (
          <div className="flex flex-col">
            <span>{buyerLabel(b.source)}</span>
            {via && (
              <span className="text-caption text-gray-mid" data-testid="buyer-created-via">{via}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "advertisement",
      header: "Advertisement",
      searchText: (b) => b.advertisementName ?? "",
      // §7 — a plain missing value. Most leads arrive from no advertisement at
      // all, and that is a fact about the lead, not a task.
      render: (b) => b.advertisementName ?? <MissingValue />,
    },
    // Only on the merged queue: on a single-team one every row would repeat the
    // same value, and §6 of the table standard is that a column which can only
    // ever say one thing is worse than no column.
    ...(showTeam
      ? [
          {
            key: "team",
            header: "Team",
            searchText: (b: BuyerLeadSummaryDto) => b.buyerTeamName ?? "",
            // 🔴 `MissingValue` rather than a blank cell OR a pill. An unrouted
            // lead is a row that escaped both the backfill and the router, so it
            // sits in nobody's scoped queue and only an all-teams holder can see
            // it at all — which makes "—" the honest rendering and this column
            // the only place it is visible.
            render: (b: BuyerLeadSummaryDto) =>
              b.buyerTeamName ? (
                <span>{b.buyerTeamName}</span>
              ) : (
                <MissingValue />
              ),
          } satisfies SubTableColumn<BuyerLeadSummaryDto>,
        ]
      : []),
    {
      key: "waiting",
      header: "Waiting since",
      render: (b) => <Timestamp value={b.createdAt} />,
    },
    {
      key: "assign",
      header: "",
      className: "text-right",
      searchText: () => "",
      render: (b) => (
        <div className="flex items-center justify-end gap-space-1">
          {showTeam && (
            <Button
              size="sm"
              variant="text"
              onClick={() => onSendToTeam(b)}
              disabled={sendingId === b.id}
              aria-label={`Send ${b.displayName} to another team`}
            >
              {sendingId === b.id ? "Sending…" : "Send to team"}
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onAssign(b)}
            disabled={assigningId === b.id}
            aria-label={`Assign ${b.displayName}`}
          >
            {assigningId === b.id ? "Assigning…" : "Assign"}
          </Button>
        </div>
      ),
    },
  ];
}

/**
 * Move ONE lead into another team's queue.
 *
 * <p>🔴 It rewrites the TEAM and nothing else. The lead keeps its specialist,
 * who may sit on a different team afterwards — the ordinary cross-team holding
 * D2 permits — and the copy says so rather than the control refusing.</p>
 */
function SendToTeamModal({
  buyer,
  teams,
  teamsFailed,
  pending,
  onCancel,
  onConfirm,
}: {
  buyer: BuyerLeadSummaryDto;
  teams: BuyerTeamSummaryDto[];
  /** The read failed, as distinct from succeeding and returning nobody. An
   *  empty list and a broken request must never render the same way. */
  teamsFailed: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (buyerTeamId: string) => void;
}) {
  const [buyerTeamId, setBuyerTeamId] = useState("");
  const destinations = teams.filter((t) => t.id !== buyer.buyerTeamId);

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Send ${buyer.displayName} to another team`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button disabled={!buyerTeamId || pending} onClick={() => onConfirm(buyerTeamId)}>
            {pending ? "Sending…" : "Send to team"}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <div className="grid gap-space-2">
          <Label required>Destination team</Label>
          {teamsFailed ? (
            <p className="m-0 text-caption text-status-error" role="alert">
              Couldn't load the team list. Close this and try again.
            </p>
          ) : destinations.length === 0 ? (
            <p className="m-0 text-caption text-gray-mid">
              There is no other open team to send this buyer to.
            </p>
          ) : (
            <select
              aria-label="Destination team"
              className="h-10 rounded-md border border-border-light bg-white-pure px-space-2 text-small"
              value={buyerTeamId}
              onChange={(e) => setBuyerTeamId(e.target.value)}
            >
              <option value="">Choose a team…</option>
              {destinations.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <p className="m-0 text-caption text-gray-mid">
          Only the queue changes. {buyer.assignedSpecialistUserId
            ? "The consultant working this buyer keeps it, even if they are on another team."
            : "This buyer has no owner yet, so it joins the destination team's unassigned queue."}
        </p>
      </div>
    </Modal>
  );
}

function SpecialistLoadTable({
  roster,
  onUnassign,
  unassigningId,
}: {
  roster: BuyerSpecialistRosterRowDto[];
  onUnassign: (buyerLeadId: string, displayName: string) => void;
  /** The buyer currently being returned to the queue, if any. */
  unassigningId: string | null;
}) {
  // Which specialist's book is open. One at a time: this is a drill-in from a
  // count, not a second list.
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <section className="mt-space-5">
      <h2 className="font-display text-h3 text-onyx mb-space-2">Consultant load</h2>
      {/*
        Raw counts, deliberately NOT an entitlement. How many buyers a specialist
        has EARNED is a different question, and PR 2b answers it — naming a
        column "entitlement" here would make 2b's arrival look like a bug fix
        rather than a new capability.

        🔴 This table is also where UNASSIGN lives, and it is the only place on
        this page it could: the queue endpoint hard-codes `Unassigned = true`, so
        no filter on the table above can ever surface a buyer that HAS an owner.
        Here the owner is the row, which makes "open their book and hand one
        back" the shape of the affordance rather than a hunt for it.
      */}
      {roster.length === 0 ? (
        /* Was `return null` — the heading and the table both disappeared, so a
           manager could not tell "nobody is set up as a specialist" from "this
           section does not exist on this screen".

           🔴 The description used to read "Nobody currently holds any buyers, so
           there is no load to report. Assign a buyer and their book appears
           here." That was true of the old group-by and false of the world: it
           described people being absent because their book was empty, which is
           exactly the bug. Now that the roster is the user table, this state has
           one meaning and says it. */
        <EmptyState
          title="No consultants to show"
          description="No user holds Buyer Consultant or Buyer Manager, so there is nobody to assign buyers to."
        />
      ) : (
      <SubTable<BuyerSpecialistRosterRowDto>
        caption="Everyone who may be given a buyer, and the book they are holding"
        data-testid="specialist-load-table"
        rows={roster}
        rowKey={(s) => s.specialistUserId}
        columns={loadColumns(expanded, setExpanded)}
        // The count opens into the thing it counted. One book at a time — this
        // is a drill-in from a number, not a second list.
        renderExpanded={(s) =>
          expanded === s.specialistUserId ? (
            <SpecialistRoster
              specialistUserId={s.specialistUserId}
              name={s.displayName ?? s.specialistUserId}
              activeBuyers={s.activeBuyers}
              onUnassign={onUnassign}
              unassigningId={unassigningId}
            />
          ) : null
        }
      />
      )}
    </section>
  );
}

/**
 * One specialist's book, loaded only once their row is opened.
 *
 * Deliberately lazy: a manager opens one book to rebalance it, so fetching every
 * specialist's roster alongside the queue would be a request per specialist for
 * a table nobody is looking at yet.
 *
 * The owner filter returns EVERY buyer they hold, including the terminal ones
 * the "Active buyers" column excludes — so the header states both figures rather
 * than letting a longer list read as a contradiction of the count above it.
 */
function SpecialistRoster({
  specialistUserId,
  name,
  activeBuyers,
  onUnassign,
  unassigningId,
}: {
  specialistUserId: string;
  name: string;
  activeBuyers: number;
  onUnassign: (buyerLeadId: string, displayName: string) => void;
  unassigningId: string | null;
}) {
  const params = useMemo<BuyerLeadListParams>(
    () => ({ assignedSpecialistUserId: specialistUserId, page: 1, pageSize: 50 }),
    [specialistUserId],
  );

  const roster = useQuery({
    queryKey: buyerKeys.list(params),
    queryFn: () => buyersApi.list(params),
  });

  if (roster.isLoading) return <Skeleton className="h-24 w-full" />;
  // Before the empty branch: "{name} is not holding any buyers" is a claim about
  // their book, and a failed read is no evidence for it.
  if (roster.isError) {
    return (
      <InlineQueryError
        what={`the buyers ${name} holds`}
        onRetry={() => void roster.refetch()}
        retrying={roster.isFetching}
      />
    );
  }

  const held = roster.data?.data ?? [];
  if (held.length === 0) {
    // The shared empty state rather than a bare `<p>`, so an empty book reads
    // the same as every other empty list in the module. Padding is trimmed for
    // the drill-in slot — this sits inside a table cell, not on a page.
    return (
      <EmptyState
        title={`${name} is not holding any buyers`}
        description="Assign one from the queue above and it appears here."
        className="py-space-4"
      />
    );
  }

  return (
    <div className="grid gap-space-2">
      <p className="text-caption text-gray-mid">
        {roster.data?.totalCount ?? held.length} held by {name} · {activeBuyers} active
      </p>
      <ul className="grid gap-space-1">
        {held.map((b) => (
          <li
            key={b.id}
            className="flex flex-wrap items-center justify-between gap-space-2 border-b border-border-light pb-space-1 last:border-b-0"
          >
            <span className="flex items-center gap-space-2">
              <Link to={`/buyers/${b.id}`} className="text-gold-shadow hover:underline">
                {b.staffDisplayName ?? b.displayName}
              </Link>
              <StatusBadge tone={buyerStatusTone(b.status)}>
                {buyerLabel(b.status)}
              </StatusBadge>
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={unassigningId === b.id}
              aria-label={`Unassign ${b.displayName}`}
              onClick={() => onUnassign(b.id, b.displayName)}
            >
              {unassigningId === b.id ? "Returning…" : "Unassign"}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A key that is STABLE for a given (specialist, selection) pair — so a user who
 * hits Assign twice, or a client that retries a timed-out request, produces the
 * same key and the server's idempotency check catches it. A random key per
 * click would defeat the mechanism entirely.
 */
function idempotencyKeyFor(specialistUserId: string, ids: Set<string>): string {
  return `bulk:${specialistUserId}:${[...ids].sort().join(",")}`;
}

/**
 * The specialist-load row.
 *
 * Raw counts, deliberately NOT an entitlement — how many buyers a specialist has
 * EARNED is a different question answered elsewhere, and a column headed
 * "entitlement" here would make that arrival look like a bug fix rather than a
 * new capability. Counts are right-aligned and tabular so a manager can compare
 * a column at a glance, which is the entire job of this table.
 */
function loadColumns(
  expanded: string | null,
  setExpanded: (id: string | null) => void,
): SubTableColumn<BuyerSpecialistRosterRowDto>[] {
  return [
    {
      key: "specialist",
      header: "Consultant",
      render: (s) => s.displayName ?? s.specialistUserId,
    },
    {
      // Managers appear here too, because a manager may take a buyer directly.
      // Labelled rather than hidden: a roster that quietly mixed the two would
      // be a roster whose rows meant different things.
      key: "role",
      header: "Role",
      searchText: (s) => ROLE_LABEL[s.buyerRoleCode],
      // Plain prose, not a pill: the role is always present, and §6 of the table
      // standard reserves pills for states worth flagging. The manager rows are
      // muted so specialists read as the default and managers as the exception.
      render: (s) => (
        <span className={s.buyerRoleCode === "F1" ? "text-gray-mid" : undefined}>
          {ROLE_LABEL[s.buyerRoleCode]}
        </span>
      ),
    },
    { key: "active", header: "Active buyers", align: "right", render: (s) => s.activeBuyers },
    { key: "qualified", header: "Qualified", align: "right", render: (s) => s.qualifiedBuyers },
    {
      key: "submitting",
      header: "Submitting",
      align: "right",
      render: (s) => s.activelySubmittingBuyers,
    },
    {
      key: "live",
      header: "Live submissions",
      align: "right",
      render: (s) => s.liveSubmissions,
    },
    {
      key: "lastAssigned",
      header: "Last assigned",
      render: (s) =>
        s.lastAssignedAtUtc ? <Timestamp value={s.lastAssignedAtUtc} /> : <MissingValue />,
    },
    {
      key: "book",
      header: "",
      className: "text-right",
      searchText: () => "",
      render: (s) => {
        const name = s.displayName ?? s.specialistUserId;
        const open = expanded === s.specialistUserId;
        return (
          <Button
            size="sm"
            variant="text"
            aria-expanded={open}
            aria-label={`${open ? "Hide" : "View"} the buyers ${name} holds`}
            onClick={() => setExpanded(open ? null : s.specialistUserId)}
          >
            {open ? "Hide buyers" : "View buyers"}
          </Button>
        );
      },
    },
  ];
}
